import React, { useState } from 'react';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import { useCache } from '../hooks/useCache';

interface PasswordPolicy {
  minimumLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSymbols: boolean;
  expiryDays: number;
  temporaryPasswordValidityDays: number;
}

// Mirrors DEFAULT_PASSWORD_POLICY in backend/functions/admin/index.js
const DEFAULTS: PasswordPolicy = {
  minimumLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSymbols: true,
  expiryDays: 0,
  temporaryPasswordValidityDays: 7
};

const STRENGTH_RULES: Array<{ key: keyof PasswordPolicy; label: string; hint: string }> = [
  { key: 'requireUppercase', label: 'Require uppercase letter', hint: 'A-Z' },
  { key: 'requireLowercase', label: 'Require lowercase letter', hint: 'a-z' },
  { key: 'requireNumbers', label: 'Require number', hint: '0-9' },
  { key: 'requireSymbols', label: 'Require symbol', hint: '!@#$…' }
];

const PasswordPolicySettings: React.FC = () => {
  const [saving, setSaving] = useState(false);

  const {
    data: storedPolicy,
    loading,
    refresh: refreshPolicy
  } = useCache(
    'admin-password-policy',
    async () => {
      try {
        return await apiCall('/admin/password-policy');
      } catch (error) {
        console.error('Falling back to default password policy:', error);
        return DEFAULTS;
      }
    },
    { ttl: 5 * 60 * 1000 }
  );

  const [settings, setSettings] = useState<PasswordPolicy>(DEFAULTS);

  React.useEffect(() => {
    if (storedPolicy) {
      setSettings({ ...DEFAULTS, ...storedPolicy });
    }
  }, [storedPolicy]);

  // Count of enabled character classes, used for the strength summary
  const enabledClasses = STRENGTH_RULES.filter(r => settings[r.key]).length;

  const strengthLabel = (() => {
    if (settings.minimumLength >= 12 && enabledClasses === 4) return { text: 'Strong', tone: 'text-green-700 bg-green-100' };
    if (settings.minimumLength >= 8 && enabledClasses >= 3) return { text: 'Moderate', tone: 'text-blue-700 bg-blue-100' };
    return { text: 'Weak', tone: 'text-red-700 bg-red-100' };
  })();

  const handleSave = async () => {
    if (enabledClasses === 0) {
      toast.error('Enable at least one character requirement');
      return;
    }

    setSaving(true);
    try {
      const response = await apiCall('/admin/password-policy', {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
      toast.success(response?.message || 'Password policy updated');
      refreshPolicy();
    } catch (error: any) {
      console.error('Error updating password policy:', error);
      toast.error(error?.message || 'Failed to update password policy');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Password Policy</h3>
          <p className="text-sm text-gray-600">
            Strength rules are enforced by Cognito whenever a password is set. Expiry is
            enforced at sign-in.
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${strengthLabel.tone}`}>
          {strengthLabel.text}
        </span>
      </div>

      <div className="space-y-6">
        {/* Minimum length */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="minimumLength">
            Minimum Length
          </label>
          <div className="flex items-center space-x-4">
            <input
              id="minimumLength"
              type="number"
              min="6"
              max="99"
              value={settings.minimumLength}
              onChange={(e) => setSettings({ ...settings, minimumLength: Number(e.target.value) })}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-sm text-gray-600">characters</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Cognito allows 6 to 99. Recommended: 12 or more.
          </p>
        </div>

        {/* Character requirements */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Character Requirements
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {STRENGTH_RULES.map(({ key, label, hint }) => (
              <label key={key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={Boolean(settings[key])}
                  onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {label} <span className="text-xs text-gray-400">({hint})</span>
                </span>
              </label>
            ))}
          </div>
          {enabledClasses === 0 && (
            <p className="text-xs text-red-600 mt-2">
              Enable at least one character requirement.
            </p>
          )}
        </div>

        {/* Expiry */}
        <div className="pt-6 border-t border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="expiryDays">
            Password Expiry
          </label>
          <div className="flex items-center space-x-4">
            <input
              id="expiryDays"
              type="number"
              min="0"
              max="3650"
              value={settings.expiryDays}
              onChange={(e) => setSettings({ ...settings, expiryDays: Number(e.target.value) })}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-sm text-gray-600">days</span>
            {settings.expiryDays > 0 ? (
              <span className="text-xs text-gray-500">
                (about {Math.round(settings.expiryDays / 30)} month{Math.round(settings.expiryDays / 30) !== 1 ? 's' : ''})
              </span>
            ) : (
              <span className="text-xs text-gray-500">(never expires)</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Set to 0 to disable expiry. Common choices are 90 or 180 days.
          </p>

          {settings.expiryDays > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
              <div className="flex">
                <svg className="h-5 w-5 text-blue-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="ml-3 text-sm text-blue-700">
                  <p>
                    Users whose password is older than {settings.expiryDays} days will be blocked
                    at sign-in and told to use <strong>Forgot Password</strong>.
                  </p>
                  <p className="mt-1 text-xs">
                    The clock starts from a user's last recorded password change. Users who
                    haven't changed their password since this feature was deployed are not
                    affected until they next change it.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Temporary password validity */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="tempValidity">
            Temporary Password Validity
          </label>
          <div className="flex items-center space-x-4">
            <input
              id="tempValidity"
              type="number"
              min="1"
              max="365"
              value={settings.temporaryPasswordValidityDays}
              onChange={(e) => setSettings({ ...settings, temporaryPasswordValidityDays: Number(e.target.value) })}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-sm text-gray-600">days</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            How long an invited user has to sign in with their temporary password before an
            admin has to reset it.
          </p>
        </div>

        {settings.minimumLength < 8 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800">Weak minimum length</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Passwords shorter than 8 characters are significantly easier to guess.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-6 border-t border-gray-200 mt-6">
        <button
          onClick={() => setSettings(DEFAULTS)}
          disabled={saving}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
        >
          Reset to Defaults
        </button>

        <button
          onClick={handleSave}
          disabled={saving || enabledClasses === 0}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving...' : 'Save Policy'}
        </button>
      </div>
    </div>
  );
};

export default PasswordPolicySettings;
